// ============================================================================
// FeatureHighlights — the six product capabilities (clients · calendar ·
// nutrition · messaging · metrics · reports/profiles), each a Pulse <Card> with
// a gradient icon chip, a blurb and three checked points. Reveals on scroll,
// staggered. Mobile-first: one column at 390, two-up from sm, three-up from lg.
// Reuses the locked design-system icons (+ inline glyphs for nutrition).
// ============================================================================
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import type { Variants } from "framer-motion";
import { Card } from "../../components";
import {
  IconUsers,
  IconCalendar,
  IconLayoutGrid,
  IconStar,
  IconMessage,
} from "../../components/icons";
import type { ReactNode } from "react";

const EASE = [0.22, 0.61, 0.36, 1] as const;

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};
const rise: Variants = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

/** Inline nutrition glyph (an apple) — no design-system icon exists for it. */
function IconNutrition({ width = 26, height = 26 }: { width?: number; height?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={width} height={height} fill="none" aria-hidden>
      <path
        d="M12 8c-1.6-1.8-5.4-1.8-6.6 1.2-1.2 3 1 7.4 3.4 8.8 1.4.8 1.6.8 3 0 2.4-1.4 4.6-5.8 3.4-8.8C17.4 6.2 13.6 6.2 12 8Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M12 8c0-2 1-3.4 3-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

interface Pillar {
  key: "clients" | "calendar" | "nutrition" | "messaging" | "metrics" | "reports";
  icon: ReactNode;
  chip: string;
  shadow: string;
}

const PILLARS: Pillar[] = [
  { key: "clients", icon: <IconUsers width={26} height={26} />, chip: "bg-grad-coral", shadow: "shadow-volt" },
  { key: "calendar", icon: <IconCalendar width={26} height={26} />, chip: "bg-grad-ocean", shadow: "shadow-ocean" },
  { key: "nutrition", icon: <IconNutrition width={26} height={26} />, chip: "bg-grad-lime", shadow: "shadow-lime" },
  { key: "messaging", icon: <IconMessage width={26} height={26} />, chip: "bg-grad-violet", shadow: "shadow-ember" },
  { key: "metrics", icon: <IconLayoutGrid width={26} height={26} />, chip: "bg-grad-ocean", shadow: "shadow-ocean" },
  { key: "reports", icon: <IconStar width={26} height={26} />, chip: "bg-grad-coral", shadow: "shadow-volt" },
];

function Check() {
  return (
    <svg viewBox="0 0 24 24" className="mt-0.5 h-4 w-4 shrink-0 text-lime-600" fill="none" aria-hidden>
      <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function FeatureHighlights() {
  const { t } = useTranslation("landing");
  const reduce = useReducedMotion();

  return (
    <section
      id="features"
      className="relative scroll-mt-20 border-y border-line bg-surface py-16 sm:py-20 lg:py-24"
    >
      {/* Soft ambient wash so the white band still feels alive. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-0 bg-canvas-glow opacity-70" />

      <div className="relative mx-auto max-w-shell px-gutter">
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="font-display text-sm font-bold uppercase tracking-kicker text-coral">
            {t("features.kicker")}
          </p>
          <h2 className="mt-3 font-display font-extrabold tracking-tightest text-body text-[clamp(1.9rem,1.2rem+3vw,3rem)]">
            {t("features.title")}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-body-muted sm:text-lg">
            {t("features.subtitle")}
          </p>
        </motion.div>

        <motion.div
          variants={reduce ? undefined : container}
          initial={reduce ? undefined : "hidden"}
          whileInView={reduce ? undefined : "show"}
          viewport={{ once: true, amount: 0.15 }}
          className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {PILLARS.map(({ key, icon, chip, shadow }) => (
            <motion.div key={key} variants={reduce ? undefined : rise} className="h-full">
              <Card interactive className="flex h-full flex-col">
                <span
                  className={
                    "mb-5 grid h-14 w-14 place-items-center rounded-md text-white " +
                    chip +
                    " " +
                    shadow
                  }
                >
                  {icon}
                </span>
                <h3 className="font-display text-xl font-bold text-body">
                  {t(`features.${key}.title`)}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-body-muted">
                  {t(`features.${key}.body`)}
                </p>
                <ul className="mt-5 space-y-2.5 border-t border-line-soft pt-5">
                  {(["a", "b", "c"] as const).map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-body-muted">
                      <Check />
                      <span>{t(`features.${key}.points.${p}`)}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
